import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import metaLogoBlack from '../../../assets/meta_logo_black.svg';
import metaSymbolBlack from '../../../assets/meta_symbol_black.svg';
import { PageToggle, type PageToggleTarget } from './PageToggle';
import { buildViewerIframeSrc, LANDING_ONLY_PARAMS } from './landingIframeSrc';
import { applyMolxyzToSearch, MOLXYZ_MESSAGE_TYPE } from '../../loader/sandboxUrlSync';
import { applyTutorialModuleToSearch, TUTORIAL_MODULE_MESSAGE_TYPE } from '../../tutorial/tutorialUrlSync';
import { withBase } from '../../../lib/withBase';

export { buildViewerIframeSrc, LANDING_ONLY_PARAMS };

// ─── Icons (inline SVGs) ────────────────────────────────────────────────────

function IconBeaker({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5.25 14.25m0 0v4.5A1.5 1.5 0 006.75 20.25h10.5a1.5 1.5 0 001.5-1.5v-4.5m-13.5 0h13.5m0 0l-3.841-3.841a2.25 2.25 0 01-.659-1.591V3.104M9.75 3.104A24.32 24.32 0 0112 3c.778 0 1.55.035 2.25.104M9.75 3.104A1.5 1.5 0 008.25 1.5h7.5a1.5 1.5 0 00-1.5 1.604" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconRx({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <text x="3" y="18" fontSize="16" fontWeight="bold" fontFamily="serif" fill="currentColor" stroke="none">Rx</text>
    </svg>
  );
}

function IconHospital({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 7.5V6m0 0V4.5M12 6h1.5M12 6h-1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconBattery({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 10.5h.375c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125H21M3.75 18h15A2.25 2.25 0 0021 15.75v-6a2.25 2.25 0 00-2.25-2.25h-15A2.25 2.25 0 001.5 9.75v6A2.25 2.25 0 003.75 18z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconDNA({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5.25 14.25" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14.25 3v5.714c0 .597.237 1.17.659 1.591L18.75 14.25" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.25 14.25v6a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5v-6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 10.5h12M6 6h12" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconLeaf({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 21c-4.97 0-9-4.03-9-9 0-2.39.94-4.68 2.64-6.36C7.68 3.68 10.3 2 13 2c3.31 0 6 2.69 6 6 0 2.76-1.52 5.17-3.78 6.44" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 21V10" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 18c.83-1.5 2-2.5 3-3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconHeart({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconGlobe({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 21a9 9 0 100-18 9 9 0 000 18z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3.6 9h16.8M3.6 15h16.8" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 3a15.3 15.3 0 014 9 15.3 15.3 0 01-4 9 15.3 15.3 0 01-4-9 15.3 15.3 0 014-9z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconFactory({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2.25 21h19.5M3.75 21V6.75l4.5 3V6.75l4.5 3V6.75l4.5 3V21" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6.75 18h.008v.008H6.75V18zm3 0h.008v.008H9.75V18zm3 0h.008v.008h-.008V18z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconPill({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4.5 12a7.5 7.5 0 0115 0M4.5 12a7.5 7.5 0 0015 0M4.5 12h15" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconAtom({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="2" />
      <ellipse cx="12" cy="12" rx="9" ry="4" />
      <ellipse cx="12" cy="12" rx="9" ry="4" transform="rotate(60 12 12)" />
      <ellipse cx="12" cy="12" transform="rotate(120 12 12)" rx="9" ry="4" />
    </svg>
  );
}

function IconShare({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconSpeed({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconModel({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconGitHub({ className = 'w-6 h-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}

function IconDownload({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconPaper({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconExternal({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function IconInfo({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ─── Data ───────────────────────────────────────────────────────────────────

// Applications: Three stories (Energy, Healthcare, Climate) each with Person → Advancement → Impact
const applicationStories = [
  // Energy Story
  {
    domain: 'energy',
    domainLabel: 'Clean Energy',
    domainColor: 'bg-amber-100 text-amber-700',
    cards: [
      {
        icon: IconBattery,
        iconColor: 'text-amber-600',
        title: 'Battery Researcher',
        description: 'A scientist at a national lab needs to find better electrode materials for next-generation batteries.',
        tag: { label: 'Person', color: 'bg-[#FFD7A7] text-[#7A5A1F]' },
      },
      {
        icon: IconSpeed,
        iconColor: 'text-amber-600',
        title: 'AI-Designed Electrolytes',
        description: 'UMA predicts electrolyte stability and ion transport properties in milliseconds, enabling rapid design of safer, higher-capacity battery materials.',
        tag: { label: 'Advancement', color: 'bg-[#CEF4B2] text-[#2D6A1E]' },
      },
      {
        icon: IconGlobe,
        iconColor: 'text-amber-600',
        title: 'Accelerated Clean Energy',
        description: 'Faster battery materials discovery speeds up electric vehicle adoption and grid-scale energy storage for renewable power.',
        tag: { label: 'Impact', color: 'bg-blue-100 text-blue-700' },
      },
    ],
  },
  // Healthcare Story
  {
    domain: 'healthcare',
    domainLabel: 'Drug Discovery',
    domainColor: 'bg-emerald-100 text-emerald-700',
    cards: [
      {
        icon: IconDNA,
        iconColor: 'text-emerald-600',
        title: 'Pharmaceutical Scientist',
        description: 'A drug discovery researcher needs to understand how candidate molecules bind to target proteins.',
        tag: { label: 'Person', color: 'bg-[#FFD7A7] text-[#7A5A1F]' },
      },
      {
        icon: IconPill,
        iconColor: 'text-emerald-600',
        title: 'Optimized Drug Candidates',
        description: 'UMA predicts binding affinities and conformational stability for thousands of drug candidates, identifying the most promising molecules for synthesis.',
        tag: { label: 'Advancement', color: 'bg-[#CEF4B2] text-[#2D6A1E]' },
      },
      {
        icon: IconHeart,
        iconColor: 'text-emerald-600',
        title: 'Faster Therapeutics',
        description: 'Accelerated drug discovery brings life-saving treatments to patients years sooner than traditional methods.',
        tag: { label: 'Impact', color: 'bg-blue-100 text-blue-700' },
      },
    ],
  },
  // Climate Story
  {
    domain: 'climate',
    domainLabel: 'Climate Action',
    domainColor: 'bg-sky-100 text-sky-700',
    cards: [
      {
        icon: IconLeaf,
        iconColor: 'text-sky-600',
        title: 'Climate Engineer',
        description: 'A researcher needs to design materials that can capture CO₂ directly from the atmosphere.',
        tag: { label: 'Person', color: 'bg-[#FFD7A7] text-[#7A5A1F]' },
      },
      {
        icon: IconFactory,
        iconColor: 'text-sky-600',
        title: 'High-Capacity Sorbent Design',
        description: 'UMA simulates thousands of interactions between porous materials and CO2 molecules to screen metal-organic frameworks (MOFs) for optimal CO₂ binding, identifying materials that capture carbon efficiently at low concentrations.',
        tag: { label: 'Advancement', color: 'bg-[#CEF4B2] text-[#2D6A1E]' },
      },
      {
        icon: IconGlobe,
        iconColor: 'text-sky-600',
        title: 'Viable Carbon Capture',
        description: 'Direct air capture becomes economically feasible, enabling removal of gigatons of CO₂ from the atmosphere.',
        tag: { label: 'Impact', color: 'bg-blue-100 text-blue-700' },
      },
    ],
  },
];

const datasets = [
  { name: 'Open Molecules 2025 (OMol25)', type: 'Drug Discovery & Biomolecules', size: '100M+ structures', theory: 'ωB97M-V/def2-TZVPD', paper: 'https://arxiv.org/abs/2505.08762', download: 'https://huggingface.co/datasets/facebook/omol25', gradient: 'linear-gradient(0deg, #54D1A4, #2787FF)' },
  { name: 'Open Polymers 2026 (OPoly26)', type: 'Polymeric Systems', size: '6.57M (1.2B atoms)', theory: 'ωB97M-V/def2-TZVPD', paper: 'https://arxiv.org/abs/2512.23117', download: 'https://huggingface.co/datasets/facebook/omol25', gradient: 'linear-gradient(0deg, #FF8F7A, #F6E64F)' },
  { name: 'Open Materials 2024 (OMat24)', type: 'Inorganic Materials', size: '110M structures', theory: 'PBE', paper: 'https://arxiv.org/abs/2410.12771', download: 'https://huggingface.co/datasets/facebook/omat24', gradient: 'linear-gradient(0deg, #7F61B4, #543A82)' },
  { name: 'Open Catalyst 2025 (OC25)', type: 'Solid-Liquid Catalysis', size: '73M structures', theory: 'RPBE+D3', paper: 'https://arxiv.org/abs/2509.17862', download: 'https://huggingface.co/datasets/facebook/oc25', gradient: 'linear-gradient(0deg, #FF8F7A, #E0402D)' },
  { name: 'Open DAC 2025 (ODAC25)', type: 'Direct Air Capture (MOFs)', size: '60M structures', theory: 'PBE+D3', paper: 'https://arxiv.org/abs/2508.03162', download: 'https://huggingface.co/datasets/facebook/odac25', gradient: 'linear-gradient(0deg, #38C8B6, #5C28B7)' },
  { name: 'Open Molecular Crystals 2025 (OMC25)', type: 'Crystal Polymorphism', size: '25M structures', theory: 'PBE+D3', paper: 'https://arxiv.org/abs/2508.02651', download: 'https://huggingface.co/datasets/facebook/OMC25', gradient: 'linear-gradient(0deg, #52CECD, #3CA4A3)' },
  { name: 'Open DAC 2023 (ODAC23)', type: 'MOFs for CO₂/H₂O Adsorption', size: '29M structures', theory: 'PBE+D3', paper: 'https://arxiv.org/abs/2311.00341', download: 'https://fair-chem.github.io/dac/datasets/odac23.html', gradient: 'linear-gradient(0deg, #FF8EC4, #62A4F9)' },
  { name: 'Open Catalyst 2022 (OC22)', type: 'Oxide Electrocatalysts', size: '~10M structures', theory: 'PBE+U', paper: 'https://arxiv.org/abs/2206.08917', download: 'https://fair-chem.github.io/catalysts/datasets/oc22.html', gradient: 'linear-gradient(0deg, #9B8CD1, #6B5B9A)' },
  { name: 'Open Catalyst 2020 (OC20)', type: 'Renewable Energy Catalysis', size: '133M+ structures', theory: 'RPBE', paper: 'https://arxiv.org/abs/2010.09990', download: 'https://fair-chem.github.io/catalysts/datasets/oc20.html', gradient: 'linear-gradient(0deg, #008DE0, #006BE7)' },
];

const resources = [
  { name: 'UMA Models', description: 'Pre-trained model weights (UMA-S, UMA-M)', url: 'https://huggingface.co/facebook/UMA', color: 'bg-blue-600' },
  { name: 'GitHub Repository', description: 'Open-source code and tutorials', url: 'https://github.com/facebookresearch/fairchem', color: 'bg-gray-800' },
  { name: 'Documentation', description: 'API reference and getting started guides', url: 'https://fair-chem.github.io/', color: 'bg-emerald-600' },
];

// ─── Navbar ─────────────────────────────────────────────────────────────────

// Camera pose for the landing hero playback canvas.
const HERO_CAMERA = {
  alpha: 1.58,
  beta: 1.18,
  radius: 18.79,
  target: [-1.46, 0.34, -0.35] as [number, number, number],
};

// URL for the landing page session file. Uses withBase so the JS
// fetch matches the relative `<link rel="preload">` in landing.html
// under any deploy base (root '/' or sub-path '/uma/').
const HERO_SESSION_URL = withBase('/sessions/session-landingpage.json');

function HeroBanner({ onNavigate }: { onNavigate: (target: PageToggleTarget) => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;
    let handle: { dispose(): void } | null = null;

    // Fire the network request immediately — this overlaps with the dynamic
    // import() parse time so both complete in parallel.
    const fetchPromise = fetch(HERO_SESSION_URL);

    const isPortrait = window.innerWidth < window.innerHeight;
    const isTablet = isPortrait && window.innerWidth >= 768;
    const camera = isTablet
      ? { ...HERO_CAMERA, radius: HERO_CAMERA.radius + 6, target: [HERO_CAMERA.target[0], HERO_CAMERA.target[1] - 2, HERO_CAMERA.target[2]] as [number, number, number] }
      : isPortrait
        ? { ...HERO_CAMERA, radius: HERO_CAMERA.radius + 16, target: [HERO_CAMERA.target[0], HERO_CAMERA.target[1] - 4.5, HERO_CAMERA.target[2]] as [number, number, number] }
        : HERO_CAMERA;

    import('../../viewer/playbackFactory').then(({ createPlaybackViewer }) => {
      if (disposed) return;
      createPlaybackViewer(canvas, {
        fetchPromise,
        sessionUrl: HERO_SESSION_URL,
        camera,
        debug: new URLSearchParams(window.location.search).get('debug') === '1',
        loop: true,
        targetFps: 25,
      }).then((h) => {
        if (disposed) { h.dispose(); return; }
        handle = h;
      }).catch((err) => {
        console.warn('[HeroBanner] playbackFactory error:', err);
      });
    }).catch((err) => {
      console.warn('[HeroBanner] playbackFactory import error:', err);
    });

    return () => {
      disposed = true;
      handle?.dispose();
    };
  }, []);

  return (
    <section className="w-full min-h-[550px] md:min-h-screen relative overflow-hidden flex flex-col bg-[#020308]">
      {/* Background: inline playback canvas (replaces the old iframe) */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ display: 'block', pointerEvents: 'none' }}
      />
      {/* Dark overlay removed — vignette handles edge darkening */}

      {/* Header bar — part of the banner */}
      <div className="relative z-10 w-full px-5 lg:px-8">
        <div className="relative flex items-start md:items-center justify-between pt-3 md:pt-0 h-16">
          <div className="flex-shrink-0">
            <div className="block">
              <div className="text-[16px] md:text-[18px] font-medium text-white leading-tight tracking-[-0.01em]">UMA playground</div>
              <div className="text-[12px] md:text-[14px] text-white/40 leading-tight">Research by Meta FAIR</div>
            </div>
          </div>
          {/* Toggle portaled from LandingPage — see below */}
          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-7">
            <a
              href="#why-ai"
              className="text-[17px] text-white hover:text-white transition-colors"
              onClick={(e) => { e.preventDefault(); document.getElementById('why-ai')?.scrollIntoView({ behavior: 'smooth' }); }}
            >
              Research
            </a>
            <a href="https://ai.meta.com/blog/meta-fair-science-new-open-source-releases/" target="_blank" rel="noopener noreferrer" className="text-[17px] text-white hover:text-white transition-colors inline-flex items-center gap-1">
              Blog <IconExternal className="w-3 h-3" />
            </a>
            <a href="https://arxiv.org/abs/2506.23971" target="_blank" rel="noopener noreferrer" className="text-[17px] text-white hover:text-white transition-colors inline-flex items-center gap-1">
              Paper <IconExternal className="w-3 h-3" />
            </a>
            <a href="https://github.com/facebookresearch/fairchem" target="_blank" rel="noopener noreferrer" className="text-white hover:text-white transition-colors">
              <IconGitHub className="w-[22px] h-[22px]" />
            </a>
          </div>
          {/* Mobile hamburger */}
          <button className="md:hidden text-white" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {menuOpen ? <path d="M6 6l12 12M6 18L18 6" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {menuOpen && (
        <div className="md:hidden relative z-10 px-5 pb-4">
          <div className="bg-black/60 backdrop-blur-md rounded-xl p-4 flex flex-col gap-3">
            <a
              href="#why-ai"
              className="text-[15px] text-white hover:text-white transition-colors"
              onClick={(e) => { e.preventDefault(); setMenuOpen(false); document.getElementById('why-ai')?.scrollIntoView({ behavior: 'smooth' }); }}
            >
              Research
            </a>
            <a href="https://ai.meta.com/blog/meta-fair-science-new-open-source-releases/" target="_blank" rel="noopener noreferrer" className="text-[15px] text-white hover:text-white transition-colors inline-flex items-center gap-1">
              Blog <IconExternal className="w-3 h-3" />
            </a>
            <a href="https://arxiv.org/abs/2506.23971" target="_blank" rel="noopener noreferrer" className="text-[15px] text-white hover:text-white transition-colors inline-flex items-center gap-1">
              Paper <IconExternal className="w-3 h-3" />
            </a>
            <a href="https://github.com/facebookresearch/fairchem" target="_blank" rel="noopener noreferrer" className="text-[15px] text-white hover:text-white transition-colors inline-flex items-center gap-1">
              <IconGitHub className="w-[18px] h-[18px]" /> GitHub
            </a>
          </div>
        </div>
      )}

      {/* Hero content */}
      <div className="relative flex-1 flex flex-col items-center justify-center text-center px-6">
        <div className="mb-4 md:mb-6">
          <IconAtom className="w-12 h-12 md:w-16 md:h-16 text-blue-400/80 mx-auto" />
        </div>
        <h1 className="text-[36px] sm:text-[48px] md:text-[64px] font-display font-normal text-white leading-tight max-w-4xl mb-4 md:mb-6" style={{ textShadow: '0 0 6px rgba(0,0,0,0.7), 0 2px 16px rgba(0,0,0,0.5), 0 4px 30px rgba(0,0,0,0.3)' }}>
          The Universal Model<br />for Atoms (UMA)
        </h1>
        <p className="text-[20px] md:text-[24px] text-white max-w-2xl mb-6 md:mb-10" style={{ textShadow: '0 0 6px rgba(0,0,0,0.7), 0 2px 16px rgba(0,0,0,0.5), 0 4px 30px rgba(0,0,0,0.3)' }}>
          A playground for AI-simulated chemistry
        </p>
        <div className="flex items-center gap-4 sm:gap-6">
          <a
            href={withBase('/playground.html')}
            onClick={(e) => { e.preventDefault(); onNavigate('playground'); }}
            className="inline-flex items-center gap-2 px-5 sm:px-8 py-3 bg-[#1C6DF9] hover:bg-[#1559D4] text-white text-[14px] sm:text-[16px] font-medium rounded-full transition-all hover:ring-2 hover:ring-white whitespace-nowrap"
          >
            Launch playground
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8h10M9 4l4 4-4 4" />
            </svg>
          </a>
          <a
            href="#why-ai"
            className="text-[14px] sm:text-[16px] text-white hover:text-white font-medium underline underline-offset-4 transition-colors whitespace-nowrap"
            style={{ textShadow: '0 0 6px rgba(0,0,0,0.7), 0 2px 16px rgba(0,0,0,0.5)' }}
            onClick={(e) => { e.preventDefault(); document.getElementById('why-ai')?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            Learn More
          </a>
        </div>
      </div>

      {/* Scroll indicator — in-flow so it never overlaps the buttons */}
      <button
        type="button"
        onClick={() => document.getElementById('why-ai')?.scrollIntoView({ behavior: 'smooth' })}
        className="relative z-10 flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors cursor-pointer pb-8 mt-auto"
        aria-label="Scroll"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="1.5em" height="1.5em" aria-hidden="true">
          <title>scroll</title>
          <path d="M13 6a1 1 0 1 0-2 0v3a1 1 0 1 0 2 0V6z" />
          <path fillRule="evenodd" clipRule="evenodd" d="M5 8a7 7 0 0 1 14 0v8a7 7 0 1 1-14 0V8zm12 0v8a5 5 0 0 1-10 0V8a5 5 0 0 1 10 0z" />
        </svg>
        <span className="text-[12px] leading-[16.4px]">Scroll</span>
      </button>
    </section>
  );
}

// ─── Section Wrapper ────────────────────────────────────────────────────────

function Section({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
  return (
    <section id={id} className={`py-16 md:py-24 px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="max-w-6xl mx-auto">
        {children}
      </div>
    </section>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[40px] font-normal font-display text-center text-gray-900 mb-4">{children}</h2>;
}

function SectionSubtitle({ children }: { children: React.ReactNode }) {
  return <p className="text-[24px] text-center text-[#676767] mb-12 max-w-2xl mx-auto">{children}</p>;
}

// ─── Stats Bar ──────────────────────────────────────────────────────────────

function StatsBar() {
  const features = [
    {
      video: withBase('/videos/benzene-break-small.mp4'),
      alt: 'Benzene bonds breaking',
      title: 'Add Atoms and Molecules',
      text: 'UMA simulations are in real time, so you can watch bonds break and form as you add new atoms or molecules.',
    },
    {
      video: withBase('/videos/waters.mp4'),
      alt: 'Water molecules simulation',
      title: 'Learn with Tutorials',
      text: 'Learn more about chemistry with AI enabled simulations, using the same cutting-edge model as researchers and scientists around the world.',
    },
    {
      video: withBase('/videos/play.mp4'),
      alt: 'Atoms launching and reacting',
      title: 'Explore and Play',
      text: 'Chemistry naturally offers the building blocks for excitement. Launch atoms and see what you can create!',
    },
  ];

  return (
    <div className="bg-white" id="why-ai">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 pt-[60px] md:pt-[115px] pb-10">
        <h2 className="text-[36px] md:text-[48px] font-display text-center text-gray-900 mb-4 leading-tight" style={{ fontWeight: 700 }}>
          The Universal Model for Atoms Powers Chemistry Simulations At Astonishing New Speeds
        </h2>
        <p className="text-[24px] md:text-[32px] text-center text-gray-900 mb-1 mt-6">
          <span id="statement-1">UMA simulates atomic and molecular interactions <span className="font-semibold" style={{ color: '#1C6DF9' }}>10,000x faster than traditional methods</span></span><a href="#footnote-1" className="text-[#1C6DF9] no-underline" style={{ fontSize: '0.6em', verticalAlign: 'super' }}>(1)</a>
        </p>
        <p className="text-[24px] md:text-[32px] text-center text-gray-900 mb-12">
          Helping transform how we can explore, teach, and learn about chemistry
        </p>

        <h2 className="text-[32px] md:text-[40px] font-display text-center text-gray-900 mb-[40px]" style={{ fontWeight: 400 }}>
          The UMA playground: A Digital<br />Lab for Chemistry
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {features.map((f: any, i) => (
            <div key={i} className="flex flex-col items-center text-center">
              {f.video ? (
                <video src={f.video} autoPlay loop muted playsInline className="w-full max-w-[360px] h-auto mb-4 object-contain rounded-lg" style={{ aspectRatio: '1' }} />
              ) : (
                <img src={f.image} alt={f.alt} className="w-full max-w-[360px] h-auto mb-4 object-contain" style={{ aspectRatio: '1' }} />
              )}
              <h3 className="text-[22px] font-bold text-gray-900 mb-2">{f.title}</h3>
              <p className="text-[16px] text-gray-600 leading-relaxed max-w-[280px]">{f.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Testimonial() {
  return (
    <div className="bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-[100px] pb-16 text-center relative">
        <div className="relative z-10">
          <p className="text-[20px] md:text-[22px] font-display text-gray-900 leading-snug">
            <span className="font-display select-none" style={{ color: '#1C6DF9', fontSize: '96px', lineHeight: 0, verticalAlign: '-0.35em', marginRight: '4px', opacity: 0.4, position: 'relative', top: '4px', left: '14px' }}>&ldquo;</span>By encouraging students to <span className="font-bold">interactively break and form chemical bonds</span> in a way that captures the underlying physics, the UMA playground opens up exciting new opportunities for students to <span className="font-bold">engage with chemistry and materials science</span> in creative ways.
          </p>
          <p className="text-[16px] md:text-[18px] text-gray-500 mt-6">
            — <span className="font-bold text-gray-700">Andrew S. Rosen</span>, Assistant Professor of Chemical and Biological Engineering, Princeton University
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Use Case Flow ──────────────────────────────────────────────────────────

function UseCaseCard({ icon, title, description, tag }: {
  icon: React.ReactNode; iconColor?: string; title: string; description: string; tag: { label: string; color: string };
  [key: string]: any;
}) {
  return (
    <div className="bg-white border border-[#D8D8D8] rounded-xl p-[16px] hover:shadow-md transition-shadow w-[260px] h-[210px]">
      <div className="flex items-start justify-between mb-3">
        {icon}
        <span className={`text-[10px] px-[8px] py-[4px] inline-flex items-center rounded-full font-medium ${tag.color}`}>
          {tag.label}
        </span>
      </div>
      <h3 className="text-[20px] font-normal text-gray-900 mb-1">{title}</h3>
      <p className="text-[12px] text-[#676767] leading-relaxed">{description}</p>
    </div>
  );
}

function RowArrow() {
  return (
    <>
      <div className="hidden min-[1150px]:flex items-center justify-center px-[16px]">
        <svg className="w-10 h-5" viewBox="0 0 40 20" fill="none">
          <path d="M0 10h36m0 0l-5-5m5 5l-5 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className="flex min-[1150px]:hidden items-center justify-center py-2">
        <svg className="w-5 h-10" viewBox="0 0 20 40" fill="none">
          <path d="M10 0v36m0 0l-5-5m5 5l5-5" stroke="#BEBEBE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </>
  );
}

// ─── Application Stories with Backgrounds ───────────────────────────────────

function UseCaseCardsRow({ cards }: { cards: { icon: React.ReactNode; title: string; description: string; tag: { label: string; color: string } }[] }) {
  return (
    <div className="flex flex-col min-[1150px]:flex-row items-center justify-center gap-0 min-[1150px]:gap-4">
      {cards.map((card, i) => (
        <Fragment key={i}>
          {i > 0 && <RowArrow />}
          <UseCaseCard
            icon={card.icon}
            iconColor="bg-blue-50"
            title={card.title}
            description={card.description}
            tag={card.tag}
          />
        </Fragment>
      ))}
    </div>
  );
}

function ApplicationStoriesSection() {
  const stories = [
    {
      label: 'Clean Energy', labelColor: 'bg-[#E8FDD8] text-[#6A8656]', bgImage: `url(${withBase('/windmills.png')}) center/cover`, video: withBase('/videos/windmills.mp4'),
      cards: [
        { icon: <img src={withBase('/flask.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Identify Viable Molecules', description: 'Use UMA to predict atomic interactions in photovoltaic materials.', tag: { label: 'Application', color: 'bg-[#FFD7A7] text-[#7A5A1F]' } },
        { icon: <img src={withBase('/rays.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Explore Solar Materials', description: 'Help identify promising solar cell candidates that may improve efficiency while reducing cost.', tag: { label: 'Advancement', color: 'bg-[#E8FDD8] text-[#6A8656]' } },
        { icon: <img src={withBase('/battery-leaf.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Unlocking Solar Potential', description: 'Explore pathways to solar energy that could be more affordable and accessible for communities around the world.', tag: { label: 'Impact', color: 'bg-blue-100 text-blue-700' } },
      ],
    },
    {
      label: 'Drug Discovery', labelColor: 'bg-[#D8F2FD] text-[#527182]', bgImage: `url(${withBase('/researcher.png')}) center/cover`, video: withBase('/videos/research.mp4'),
      cards: [
        { icon: <img src={withBase('/flask.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Model Ligand-Protein Interactions', description: 'UMA is able to model ligand-protein interactions that can reduce computational cost through speed improvements.', tag: { label: 'Application', color: 'bg-[#FFD7A7] text-[#7A5A1F]' } },
        { icon: <img src={withBase('/prescription-rx.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Support Smarter Drug Screening', description: 'Inform clinical trial design for virtual drug screening.', tag: { label: 'Advancement', color: 'bg-[#E8FDD8] text-[#6A8656]' } },
        { icon: <img src={withBase('/hospital-building.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Exploring Health and Wellbeing', description: 'Unlock insights across clinical trial performance to help accelerate drug discovery for patients who need them.', tag: { label: 'Impact', color: 'bg-blue-100 text-blue-700' } },
      ],
    },
    {
      label: 'Climate Action', labelColor: 'bg-[#FDD8FD] text-[#865677]', bgImage: `url(${withBase('/forest.png')}) center/cover`, video: withBase('/videos/flowers.mp4'),
      cards: [
        { icon: <img src={withBase('/flask.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Simulate Molecular Interactions', description: 'Simulate how molecules bind to catalyst surfaces at 10,000x the speed of traditional methods.', tag: { label: 'Application', color: 'bg-[#FFD7A7] text-[#7A5A1F]' } },
        { icon: <img src={withBase('/bolt.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Identify Novel Catalysts', description: 'Discover potential new catalysts that convert sunlight and water into clean hydrogen fuel.', tag: { label: 'Advancement', color: 'bg-[#E8FDD8] text-[#6A8656]' } },
        { icon: <img src={withBase('/branch-check-black.svg')} alt="" style={{ width: 24, height: 24 }} />, title: 'Advancing Green Hydrogen Research', description: 'Help speed up the discovery of materials that may enable cleaner hydrogen production for industry and transportation.', tag: { label: 'Impact', color: 'bg-blue-100 text-blue-700' } },
      ],
    },
  ];
  return (
    <div className="bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-[40px] font-display font-normal text-center text-gray-900 mb-4 leading-tight max-w-2xl mx-auto">
          How AI Can Help Research and Industry Experts Tackle Real-World Problems
        </h2>
        <p className="text-[20px] text-center text-gray-900 mb-[40px] font-normal">Every material, medicine, and molecule starts with atoms. UMA was trained across many molecular and materials domains, which enabled the exploration of potential new solutions in areas like clean energy, drug discovery, and climate action. Some of these possibilities include:</p>
        <div className="space-y-12">
          {stories.map((story, idx) => (
            <div key={idx}>
              {/* Desktop: background video with cards overlay */}
              <div className="hidden min-[1150px]:block relative rounded-2xl overflow-hidden" style={{ height: 364 }}>
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                  src={story.video}
                />
                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute top-0 left-0 right-0 h-[110px] flex items-center justify-center" style={{ zIndex: 1 }}>
                  <span className={`text-[13px] px-[10px] py-[5px] rounded-full font-medium ${story.labelColor}`}>
                    {story.label}
                  </span>
                </div>
                <div className="relative h-full pt-[56px] px-6 flex items-center justify-center">
                  <UseCaseCardsRow cards={story.cards} />
                </div>
              </div>
              {/* Mobile: stacked cards, no background image */}
              <div className="min-[1150px]:hidden p-4">
                <div className="flex justify-center mb-4">
                  <span className={`text-[13px] px-[10px] py-[5px] rounded-full font-medium ${story.labelColor}`}>
                    {story.label}
                  </span>
                </div>
                <UseCaseCardsRow cards={story.cards} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Open Science Section ───────────────────────────────────────────────────

function OpenScienceSection() {
  const cards = [
    { icon: withBase('/people-group.svg'), category: 'Models', title: 'Download the UMA Model', description: 'The UMA model was designed for researchers and industry professionals in materials science. Use UMA in support of your own research to discover new breakthrough materials.', url: 'https://huggingface.co/facebook/UMA' },
    { icon: withBase('/handshake.svg'), category: 'Datasets', title: 'Explore the OMol Data', description: 'Access one of the largest open datasets of atomic structures ever created and released.', url: 'https://huggingface.co/facebook/OMol25' },
  ];
  return (
    <Section className="bg-white !pt-[40px] !pb-0">
      <h2 className="text-[40px] font-display font-normal text-center text-gray-900 mb-4 leading-tight">
        Our Commitment to<br />Open Science and Safety
      </h2>
      <p className="text-[20px] text-center text-gray-900 mb-10 max-w-3xl mx-auto font-normal leading-relaxed">
        Great science is built in the open. UMA stands on shared research, and we're giving back. We've made our models, datasets, and code openly available to move scientific discovery forward, together.
      </p>
      <p className="text-[20px] text-center text-gray-900 mb-10 max-w-3xl mx-auto font-normal leading-relaxed">
        We take proactive steps to build in safeguards during development, working with experts and others in the industry to mitigate risks. We train our models on molecules with well-established safety profiles, display results as relative scores rather than raw calculations in our demo, and incorporate built-in precision guardrails to ensure demo usability without over-exposing sensitive details.
      </p>
      <h3 className="text-[28px] font-display font-normal text-center text-gray-900 mb-4">
        Resources for Research and Industry
      </h3>
      <p className="text-[20px] text-center text-gray-900 mb-10 max-w-3xl mx-auto font-normal leading-relaxed">
        This demo is powered by UMA – a leading AI model used by expert scientists to make breakthroughs in new molecular design and materials discovery. Researchers across both research and industry can explore further applications of the work behind this demo by downloading the model and its training data detailed below.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[700px] mx-auto">
        {cards.map((card, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 text-left flex flex-col">
            <img src={card.icon} alt="" className="w-[48px] h-[48px] mb-6 mx-auto" />
            {(card as any).category && <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">{(card as any).category}</p>}
            <h4 className="font-medium text-[14px] text-gray-900 mb-1">{card.title}</h4>
            <p className="text-[12px] text-[#676767] mb-4 flex-1">{card.description}</p>
            <a
              href={card.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] font-normal text-white bg-[#1C6DF9] hover:bg-[#1559D4] px-4 py-2 rounded-lg transition-colors w-fit"
            >
              <IconDownload className="w-3.5 h-3.5" />
              Download
            </a>
          </div>
        ))}
      </div>
      <p className="text-[16px] text-center text-gray-400 mt-12 max-w-2xl mx-auto">
        Meta invests in fundamental AI research in chemistry because solving the world's hardest problems — from energy to health — starts at the atomic level.
      </p>
      <div className="mt-12 mb-32">
        <h2 className="text-[40px] font-display font-normal text-center text-gray-900 mb-6">Explore More</h2>
        <p className="text-[20px] text-center text-gray-900 max-w-3xl mx-auto mb-10 font-normal leading-relaxed">
          We've published a full technical paper on this work with scientists across industry and academia, which you can read <a href="https://arxiv.org/abs/2506.23971" target="_blank" rel="noopener noreferrer" className="text-[#1C6DF9] hover:underline">here</a>.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="https://huggingface.co/spaces/facebook/fairchem_uma_demo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[16px] font-medium text-white bg-[#1C6DF9] hover:bg-[#1559D4] px-[21px] py-[8px] rounded-full transition-colors">
            <img src={withBase('/code.svg')} alt="" className="w-5 h-5 invert" /> Try our Technical Demo
          </a>
          <a href="https://arxiv.org/abs/2506.23971" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[16px] font-medium text-white bg-[#1C6DF9] hover:bg-[#1559D4] px-[21px] py-[8px] rounded-full transition-colors whitespace-nowrap">
            <img src={withBase('/document.svg')} alt="" className="w-5 h-5 invert" /> Read our Academic Paper about UMA
          </a>
          <a href="mailto:uma@meta.com" className="inline-flex items-center gap-2 text-[16px] font-medium text-white bg-[#1C6DF9] hover:bg-[#1559D4] px-[21px] py-[8px] rounded-full transition-colors">
            <img src={withBase('/envelop.svg')} alt="" className="w-5 h-5 invert" /> Have Feedback? Email Us
          </a>
        </div>
        <p id="footnote-1" className="text-[16px] text-center text-gray-400 mt-12 max-w-2xl mx-auto">
          <a href="#statement-1" className="text-gray-400 no-underline hover:underline">(1)</a> UMA's average speedup is calculated across specific molecular systems such as ML-PES and Carbenes, and can vary across diverse systems. These are relative to Density Functional Theory. UMA approximates quantum chemistry remarkably well, but not perfectly. Results are predictions, not exact calculations, and this demo is simplified for educational exploration.
        </p>
      </div>
    </Section>
  );
}

// ─── Open Datasets ──────────────────────────────────────────────────────────

function DatasetCard({ dataset }: { dataset: typeof datasets[0] }) {
  return (
    <div className="bg-white rounded-xl border border-[#D8D8D8] overflow-hidden hover:shadow-md transition-shadow w-[280px]">
      <div
        className="text-white text-[14px] font-medium px-3 h-[56px] flex items-center justify-center gap-1.5"
        style={{ background: dataset.gradient }}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {dataset.size}
      </div>
      <div className="p-4 pb-[16px]">
        <h4 className="font-semibold text-[12px] text-gray-900 mb-0.5">{dataset.name}</h4>
        <p className="text-[11px] text-[#676767] mb-2">{dataset.type}</p>
        <div className="flex gap-1.5 mb-[16px]">
          <span className="text-[10px] px-[8px] py-[4px] rounded-[5px] bg-[#CECECE] text-[#646464] font-medium">{dataset.theory}</span>
        </div>
        <div className="flex gap-2">
          <a
            href={dataset.paper}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] font-normal text-white bg-[#1C6DF9] hover:bg-[#1558d4] p-[8px] rounded-lg transition-colors"
          >
            <IconPaper className="w-3 h-3" />
            Paper
          </a>
          <a
            href={dataset.download}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[12px] font-normal text-[#1C6DF9] hover:text-[#1558d4] px-2.5 py-1 rounded-lg transition-colors"
          >
            <IconDownload className="w-3 h-3" />
            Download
          </a>
        </div>
      </div>
    </div>
  );
}

function OpenDatasetsSection() {
  return (
    <Section className="bg-white">
      <SectionTitle>Open Datasets</SectionTitle>
      <div className="h-8" />
      <div className="flex flex-wrap justify-center gap-x-[32px] gap-y-6 max-w-[814px] mx-auto">
        {datasets.map((dataset, i) => (
          <DatasetCard key={i} dataset={dataset} />
        ))}
      </div>
    </Section>
  );
}

// ─── Resources and Access ───────────────────────────────────────────────────

function ResourcesSection() {
  return (
    <Section>
      <SectionTitle>Resources and Access</SectionTitle>
      <SectionSubtitle>Code to use the UMA for your research or education</SectionSubtitle>
      <div className="flex flex-wrap justify-center gap-x-[32px] gap-y-6">
        {resources.map((resource, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#D8D8D8] p-[16px] text-left w-[250px] overflow-hidden">
            <h4 className="font-medium text-[12px] text-gray-900 mb-1">{resource.name}</h4>
            <p className="text-[12px] text-[#676767] mb-3">{resource.description}</p>
            <a
              href={resource.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 text-[12px] font-normal text-white ${resource.color} hover:opacity-90 px-3 py-1.5 rounded-lg transition-opacity`}
            >
              <IconDownload className="w-3.5 h-3.5" />
              Access
            </a>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─── Footer ─────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="bg-white border-t border-gray-200 py-8">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[14px] text-gray-400">
            <span className="whitespace-nowrap">&copy; 2026 Meta</span>
            <a href={withBase('/terms.html')} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">Terms</a>
            <a href={withBase('/usage.html')} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">Usage</a>
            <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">Privacy</a>
            <a href="https://www.facebook.com/privacy/policies/cookies" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">Cookies</a>
          </div>
          <div className="flex items-center gap-3">
            <img src={withBase('/meta.svg')} alt="Meta" className="w-8 h-8" />
            <div className="text-[13px]">
              <span className="text-gray-400">Learn more</span>
              <a href="https://ai.meta.com/research/" target="_blank" rel="noopener noreferrer" className="block text-gray-900 font-medium hover:text-[#1C6DF9] transition-colors">
                ai.meta.com/research <span className="text-xs text-gray-400">↗</span>
              </a>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <a href="https://facebook.com/aiatmeta" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-gray-600 transition-colors" aria-label="Facebook">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.875V12h3.328l-.532 3.47h-2.796v8.385C19.612 22.954 24 17.99 24 12z"/></svg>
            </a>
            <a href="https://threads.com/aiatmeta" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-gray-600 transition-colors" aria-label="Threads">
              <img src={withBase('/threads.svg')} alt="Threads" className="w-6 h-6" />
            </a>
            <a href="https://www.linkedin.com/showcase/aiatmeta/" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-gray-600 transition-colors" aria-label="LinkedIn">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </a>
            <a href="https://x.com/aiatmeta" target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:text-gray-600 transition-colors" aria-label="X">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Landing Page ──────────────────────────────────────────────────────

/**
 * Read the current view from the URL. We use a `?view=playground`
 * query param so the page works as a single HTML entry-point
 * (`/` / `/index.html`) and the playground is opened in-page rather
 * than via a hard navigation to `/playground.html`.
 *
 * Anything that isn't an exact match for `playground` is treated as
 * the about/landing view — including the empty/missing case.
 */
function readViewFromUrl(): PageToggleTarget {
  if (typeof window === 'undefined') return 'about';
  const v = new URLSearchParams(window.location.search).get('view');
  return v === 'playground' ? 'playground' : 'about';
}

/**
 * Build a URL string that preserves all existing query params but
 * sets/removes `view=playground` to match the target view. Used for
 * `history.pushState` so the browser back button moves between
 * about ⇄ playground without a reload.
 */
function urlForView(target: PageToggleTarget): string {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  if (target === 'playground') url.searchParams.set('view', 'playground');
  else url.searchParams.delete('view');
  return url.pathname + (url.search ? url.search : '') + url.hash;
}

export function LandingPage() {
  const [view, setView] = useState<PageToggleTarget>(() => readViewFromUrl());

  // Once the playground view has been requested at least once, keep
  // the iframe mounted forever. This is the whole point of the
  // optimization: the second toggle (and every one after) is
  // instant because we never destroy the viewer.
  const [playgroundMounted, setPlaygroundMounted] = useState<boolean>(
    () => readViewFromUrl() === 'playground',
  );

  // Ref to the embedded playground <iframe>. Used by the postMessage
  // handler below to gate `mlipview:navigate` events to messages that
  // originated from THIS iframe (not from arbitrary cross-frame
  // siblings or third-party embeds).
  const playgroundIframeRef = useRef<HTMLIFrameElement | null>(null);

  // Resolve the playground iframe src lazily on first mount. Built
  // from the current URL params (minus `view`) via the same builder
  // the existing background iframe uses, so deep-link params keep
  // flowing through.
  const playgroundSrcRef = useRef<string | null>(null);
  if (playgroundMounted && playgroundSrcRef.current === null) {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      params.delete('view'); // landing-only key; viewer ignores it anyway
      playgroundSrcRef.current = buildViewerIframeSrc(params);
    } else {
      playgroundSrcRef.current = withBase('/playground.html?embed=1');
    }
  }

  const navigateTo = useCallback((target: PageToggleTarget) => {
    setView(prev => {
      if (prev === target) return prev;
      try {
        window.history.pushState({ view: target }, '', urlForView(target));
      } catch {
        // ignore — view state still updates even if history fails
      }
      return target;
    });
    if (target === 'playground') setPlaygroundMounted(true);
  }, []);

  // Sync view with browser back/forward (popstate fires for both).
  useEffect(() => {
    const onPop = () => {
      const next = readViewFromUrl();
      setView(next);
      if (next === 'playground') setPlaygroundMounted(true);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Listen for navigate requests from the embedded playground iframe
  // (its PageToggle posts `mlipview:navigate` when clicked from
  // inside an iframe — see PageToggle.tsx).
  //
  // Gate the message on origin AND source: it must come from our own
  // origin (the iframe is same-origin by design) AND specifically from
  // the playground iframe's contentWindow, not from any other framed
  // context. Without these checks, a third party that frames the
  // landing page could force-mount the playground and start a WS/GPU
  // session by posting a single message.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const iframeWin = playgroundIframeRef.current?.contentWindow;
      if (iframeWin && e.source !== iframeWin) return;
      const data = e.data;
      if (!data || typeof data !== 'object') return;
      // The embedded playground mirrors its sandbox build up to us so it lands
      // in the visible (landing) address bar instead of the hidden iframe URL.
      if (data.type === MOLXYZ_MESSAGE_TYPE) {
        try {
          const search = applyMolxyzToSearch(
            window.location.search,
            typeof data.value === 'string' ? data.value : null,
          );
          window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${search}${window.location.hash}`,
          );
        } catch {
          /* ignore */
        }
        return;
      }
      // Same idea for the active tutorial: the embedded playground mirrors its
      // module id up so the visible URL stays a shareable deep link to it.
      // Every landing param is forwarded into the iframe src, so the link
      // reopens the tutorial.
      if (data.type === TUTORIAL_MODULE_MESSAGE_TYPE) {
        try {
          const search = applyTutorialModuleToSearch(
            window.location.search,
            typeof data.value === 'string' ? data.value : null,
          );
          window.history.replaceState(
            window.history.state,
            '',
            `${window.location.pathname}${search}${window.location.hash}`,
          );
        } catch {
          /* ignore */
        }
        return;
      }
      if (data.type !== 'mlipview:navigate') return;
      const target = data.target;
      if (target === 'about' || target === 'playground') navigateTo(target);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigateTo]);

  // Lock body scroll while the playground is full-screen — otherwise
  // the (still-rendered) about content underneath can be scrolled by
  // wheel events that don't hit the iframe.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = view === 'playground' ? 'hidden' : prev;
    return () => { document.body.style.overflow = prev; };
  }, [view]);

  const showPlayground = view === 'playground';

  return (
    <>
      {/* About / Playground toggle — portaled to body so it sits above
          all page content.  Tracks the current view and switches
          variant (dark on about, light on playground). Hidden when the
          playground iframe renders its own toggle. */}
      {createPortal(
        <div
          className="hidden not-phone:block fixed top-[80px] min-[900px]:top-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-auto"
          style={{ display: showPlayground ? 'none' : undefined }}
        >
          <PageToggle active={view} variant="dark" onNavigate={navigateTo} />
        </div>,
        document.body,
      )}

      {/* About page — kept mounted (display:none toggle) so React
          state, scroll position, and the cinematic background iframe
          all survive across view swaps. */}
      <div
        className="min-h-screen bg-white"
        style={{ display: showPlayground ? 'none' : undefined }}
        aria-hidden={showPlayground || undefined}
      >
        <HeroBanner onNavigate={navigateTo} />
        <StatsBar />
        <Testimonial />
        <ApplicationStoriesSection />
        <OpenScienceSection />
        <Footer />
      </div>

      {/* Playground iframe — lazily mounted on first toggle, then
          kept mounted forever so subsequent toggles are instant. */}
      {playgroundMounted && (
        <iframe
          ref={playgroundIframeRef}
          src={playgroundSrcRef.current ?? withBase('/playground.html?embed=1')}
          title="UMA playground"
          // Dynamic viewport units (`dvw`/`dvh`) instead of `vw`/`vh` so the
          // iframe tracks the visual viewport as the iOS Safari URL bar shows
          // and hides. Plain `100vh` is the *layout* viewport (URL-bar-
          // collapsed) height, so on iPhone the iframe ran past the visible
          // edge and the playground's bottom-bar got painted behind the URL
          // bar / Safari toolbar.
          className="fixed inset-0 w-dvw h-dvh border-0 z-[100]"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; fullscreen"
          allowFullScreen
          style={{
            display: showPlayground ? 'block' : 'none',
            // visibility:hidden would also cut paint cost but display:none
            // releases the iframe entirely from the layout pass while
            // preserving its scripting context, which is what we want.
          }}
        />
      )}
    </>
  );
}
